import React, { useEffect, useMemo, useState } from "react";
import { Line } from "@ant-design/plots";

import { TrendingMarketData } from "../shared/type";

const RANGE_OPTIONS = [
  { id: "all", label: "All", days: null as number | null },
  { id: "1w", label: "1W", days: 7 },
  { id: "1m", label: "1M", days: 30 },
  { id: "3m", label: "3M", days: 90 },
];

function filterPointsByRange(
  points: Array<[number, number]>,
  days: number | null,
): Array<[number, number]> {
  if (!days || points.length === 0) return points;
  const latest = points[points.length - 1]?.[0] ?? Date.now();
  const minTs = latest - days * 24 * 60 * 60 * 1000;
  return points.filter(([ts]) => ts >= minTs);
}

function formatAxisMonth(value: Date | number | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function formatTooltipDate(value: Date | number | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatUsd(value: number): string {
  return `US $${Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
}

const PriceChart: React.FC<{
  points: Array<[number, number]>;
}> = ({ points }) => {
  const { data, domainMin, domainMax } = useMemo(() => {
    const mapped = points.map(([ts, price]) => ({
      date: new Date(ts),
      price,
    }));
    const times = points.map(([ts]) => ts);
    return {
      data: mapped,
      domainMin: new Date(Math.min(...times)),
      domainMax: new Date(Math.max(...times)),
    };
  }, [points]);

  if (points.length < 2) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-slate-400 border border-slate-200 rounded-lg bg-slate-50">
        No chart data
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border-0 border-slate-200 bg-white px-0 pt-0 pb-1">
      <Line
        data={data}
        xField="date"
        yField="price"
        height={260}
        autoFit
        // Leave room for Y labels (left) and the last X label (right),
        // otherwise the end tick sits on the canvas edge and gets clipped.
        marginLeft={6}
        marginRight={24}
        marginTop={8}
        marginBottom={4}
        shape="smooth"
        style={{
          stroke: "#F05A2A",
          lineWidth: 2,
        }}
        scale={{
          // Keep X domain exactly on data so empty months are not padded in.
          x: {
            type: "time",
            nice: false,
            domainMin,
            domainMax,
            tickCount: 8,
          },
          y: { domainMin: 0, nice: true },
        }}
        axis={{
          x: {
            title: false,
            labelFormatter: formatAxisMonth,
            labelAutoRotate: false,
            labelAutoHide: true,
            labelAutoEllipsis: true,
            tickFilter: (datum: Date | number | string) => {
              const ts = new Date(datum).getTime();
              return ts >= domainMin.getTime() && ts <= domainMax.getTime();
            },
            line: false,
            grid: false,
            labelFill: "#000000",
            labelFillOpacity: 1,
          },
          y: {
            title: false,
            labelFormatter: (value: number) => formatUsd(value),
            labelAutoEllipsis: true,
            grid: {
              stroke: "#e2e8f0",
              lineWidth: 1,
            },
            line: false,
            labelFill: "#000000",
            labelFillOpacity: 1,
          },
        }}
        tooltip={{
          title: (datum: { date?: Date }) =>
            formatTooltipDate(datum?.date ?? ""),
          items: [
            {
              channel: "y",
              name: "Price",
              valueFormatter: (value: number) => formatUsd(value),
            },
          ],
        }}
        interaction={{
          tooltip: {
            marker: false,
            crosshairs: true,
            crosshairsStroke: "#94a3b8",
            crosshairsLineDash: [4, 4],
          },
        }}
      />
    </div>
  );
};

const TrendingMarketPanel: React.FC<{ productId: string }> = ({
  productId,
}) => {
  const [data, setData] = useState<TrendingMarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeId, setRangeId] = useState("all");
  const [sizeKey, setSizeKey] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    setRangeId("all");
    setSizeKey("all");

    fetch(`/trending-market/${productId}.json`)
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json()) as TrendingMarketData;
      })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const sizeOptions = useMemo(() => {
    if (!data) return [{ key: "all", label: "All Sizes" }];
    return [
      { key: "all", label: "All Sizes" },
      ...data.sizes.map((size) => ({
        key: String(size.productSizeId),
        label: size.label,
      })),
    ];
  }, [data]);

  const activeRange = RANGE_OPTIONS.find((range) => range.id === rangeId);
  const series = data?.charts?.[rangeId]?.[sizeKey];
  const chartPoints = filterPointsByRange(
    series?.points ?? [],
    activeRange?.days ?? null,
  );

  if (loading) {
    return (
      <section className="w-full max-w-[80%] border-t-0 border-slate-200 pt-6">
        <p className="text-sm text-slate-500">Loading market data...</p>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  const hasHistory = data.history.length > 0;
  const hasChart = chartPoints.length > 0;

  if (!hasHistory && !hasChart) {
    return (
      <section className="w-full max-w-[80%] border-t-0 border-slate-200 py-2">
        <p className="text-sm text-slate-500">
          No data available, please try again later or view other products.
        </p>
      </section>
    );
  }

  return (
    <section className="w-full max-w-full md:max-w-[80%] border-t-0 border-slate-200 pt-0 flex flex-col gap-8">
      {hasHistory ? (
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 mb-3">
            Trading History
          </h2>
          <div className="max-h-56 overflow-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-[-1px] z-10 bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Date</th>
                  <th className="text-left font-medium px-3 py-2">
                    Cond / Qty
                  </th>
                  <th className="text-right font-medium px-3 py-2">Price</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((row, index) => (
                  <tr
                    key={`${row.tradedAt}-${row.qty}-${index}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2 text-slate-700">{row.date}</td>
                    <td className="px-3 py-2 text-slate-700">{row.qty}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">
                      {row.priceFormat}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {hasChart || sizeOptions.length > 0 ? (
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 mb-3">
            Price Chart
          </h2>

          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4 border-b border-slate-200">
            {sizeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSizeKey(option.key)}
                className={`pb-2 text-sm transition-colors border-b-2 -mb-px ${
                  sizeKey === option.key
                    ? "border-slate-900 text-slate-900 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex justify-end mb-3">
            <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setRangeId(option.id)}
                  className={`min-w-[2.75rem] px-2.5 py-1 text-xs rounded transition-colors ${
                    rangeId === option.id
                      ? "bg-white text-slate-900 font-semibold shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <PriceChart points={chartPoints} />
          <p className="mt-1 text-[11px] text-slate-400">
            Prices converted to USD
            {data.sourceCurrency && data.sourceCurrency !== "USD"
              ? ` from ${data.sourceCurrency}`
              : ""}
            . Origin: Snkrdunk.com
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default TrendingMarketPanel;
